import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import toast from "react-hot-toast";
import { ImagePlus, Pencil, Plus, Trash2, X } from "lucide-react";
import { normalizeQuickMessageShortcut } from "../lib/quickMessages.mjs";

type TemplateKind = "whatsapp" | "quick_message";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Clinic {
  id: string;
  raison_sociale: string | null;
  nom: string | null;
  prenom: string | null;
}

interface Template {
  id?: string;
  praticien_id: string;
  template_kind: TemplateKind;
  shortcut: string;
  name: string;              // = chakra_template_name (Meta name)
  display_name: string;      // label shown in the front
  template_body: string;
  placeholders: string[];
  language_code: string;
  content_sid: string;       // Twilio only
  header_media_url: string;
  chakra_template_name?: string | null;
}

const EMPTY_TEMPLATE = (
  praticien_id: string,
  template_kind: TemplateKind = "whatsapp"
): Template => ({
  praticien_id,
  template_kind,
  shortcut: "",
  name: "",
  display_name: "",
  template_body: "",
  placeholders: [],
  language_code: template_kind === "whatsapp" ? "en" : "",
  content_sid: "",
  header_media_url: "",
  chakra_template_name: "",
});

function clinicLabel(c: Clinic) {
  return c.raison_sociale || [c.prenom, c.nom].filter(Boolean).join(" ") || c.id;
}

function normalizeTemplate(row: any, selectedClinicId: string): Template {
  const templateKind = (row?.template_kind ?? "whatsapp") as TemplateKind;

  return {
    ...EMPTY_TEMPLATE(row?.praticien_id ?? selectedClinicId, templateKind),
    ...row,
    template_kind: templateKind,
    shortcut: row?.shortcut ?? "",
    name: row?.name ?? row?.chakra_template_name ?? "",
    placeholders: Array.isArray(row?.placeholders) ? row.placeholders : [],
    chakra_template_name: row?.chakra_template_name ?? row?.name ?? "",
  };
}

function isQuickMessage(template: Template) {
  return template.template_kind === "quick_message";
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Templates() {
  const user = useUser();

  // Selected clinic (admin picks from all clinics)
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState<string>(() => {
    const stored = localStorage.getItem("activeDoctor");
    return stored ? JSON.parse(stored)?.id ?? "" : "";
  });

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<Template>(EMPTY_TEMPLATE(""));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [placeholderInput, setPlaceholderInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Load clinics ───────────────────────────────────────────────────────────

  useEffect(() => {
    async function loadClinics() {
      const { data, error } = await supabase
        .from("clients")
        .select("id, raison_sociale, nom, prenom")
        .order("raison_sociale", { ascending: true });
      if (error) toast.error("Failed to load clinics");
      else {
        setClinics(data ?? []);
        if (!selectedClinicId && data && data.length > 0) {
          setSelectedClinicId(data[0].id);
        }
      }
      setLoadingClinics(false);
    }
    loadClinics();
  }, []); // eslint-disable-line

  // ─── Load templates ─────────────────────────────────────────────────────────

  async function loadTemplates(clinicId = selectedClinicId) {
    if (!clinicId) return;
    setLoadingTemplates(true);
    const { data, error } = await supabase
      .from("clinic_templates")
      .select("*")
      .eq("praticien_id", clinicId)
      .order("display_name");
    if (error) toast.error("Failed to load templates");
    else setTemplates((data ?? []).map((row) => normalizeTemplate(row, clinicId)));
    setLoadingTemplates(false);
  }

  useEffect(() => {
    if (selectedClinicId) loadTemplates(selectedClinicId);
    else setTemplates([]);
  }, [selectedClinicId]); // eslint-disable-line

  // ─── Panel helpers ──────────────────────────────────────────────────────────

  function openNew(templateKind: TemplateKind) {
    setEditing(EMPTY_TEMPLATE(selectedClinicId, templateKind));
    setPlaceholderInput("");
    setPanelOpen(true);
  }

  function openEdit(tpl: Template) {
    setEditing(normalizeTemplate(tpl, selectedClinicId));
    setPlaceholderInput("");
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditing(EMPTY_TEMPLATE(selectedClinicId));
    setPlaceholderInput("");
  }

  // ─── Placeholders ───────────────────────────────────────────────────────────

  function addPlaceholder() {
    const val = placeholderInput.trim().replace(/\s+/g, "_");
    if (!val || editing.placeholders.includes(val)) return;
    setEditing(prev => ({ ...prev, placeholders: [...prev.placeholders, val] }));
    setPlaceholderInput("");
  }

  function removePlaceholder(p: string) {
    setEditing(prev => ({ ...prev, placeholders: prev.placeholders.filter(x => x !== p) }));
  }

  // ─── Image upload ───────────────────────────────────────────────────────────

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const path = `${selectedClinicId}/${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from("template-media")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (uploadError) {
      toast.error("Upload error: " + uploadError.message);
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("template-media").getPublicUrl(path);
    setEditing(prev => ({ ...prev, header_media_url: urlData.publicUrl }));
    setUploading(false);
    toast.success("Image uploaded");
  }

  // ─── Save ───────────────────────────────────────────────────────────────────

  async function handleSave() {
    const quickMessage = isQuickMessage(editing);
    const normalizedShortcut = normalizeQuickMessageShortcut(editing.shortcut);

    if (!editing.display_name.trim()) {
      toast.error("Display name is required");
      return;
    }
    if (quickMessage && !normalizedShortcut) {
      toast.error("Shortcut is required");
      return;
    }
    if (quickMessage && !editing.template_body.trim()) {
      toast.error("Message body is required");
      return;
    }
    if (!quickMessage && !editing.name.trim()) {
      toast.error("Meta template name is required");
      return;
    }
    if (!selectedClinicId) {
      toast.error("Select a clinic first");
      return;
    }
    setSaving(true);
    const payload = quickMessage
      ? {
          praticien_id: selectedClinicId,
          template_kind: "quick_message" as const,
          shortcut: normalizedShortcut,
          name: "",
          display_name: editing.display_name.trim(),
          chakra_template_name: "",
          template_body: editing.template_body.trim(),
          placeholders: [],
          language_code: "",
          content_sid: "",
          header_media_url: "",
        }
      : {
          praticien_id: selectedClinicId,
          template_kind: "whatsapp" as const,
          shortcut: null,
          name: editing.name.trim(),
          display_name: editing.display_name.trim(),
          chakra_template_name: editing.name.trim(),
          template_body: editing.template_body,
          placeholders: editing.placeholders,
          language_code: editing.language_code.trim() || "en",
          content_sid: editing.content_sid.trim(),
          header_media_url: editing.header_media_url.trim(),
        };
    const { error } = editing.id
      ? await supabase.from("clinic_templates").update(payload).eq("id", editing.id)
      : await supabase.from("clinic_templates").insert(payload);
    if (error) {
      toast.error("Error: " + error.message);
    } else {
      toast.success(
        editing.id
          ? quickMessage
            ? "Quick message updated"
            : "Template updated"
          : quickMessage
            ? "Quick message created"
            : "Template created"
      );
      closePanel();
      loadTemplates();
    }
    setSaving(false);
  }

  // ─── Delete ─────────────────────────────────────────────────────────────────

  async function handleDelete(tpl: Template) {
    if (!window.confirm(`Delete "${tpl.display_name}"?`)) return;
    const { error } = await supabase.from("clinic_templates").delete().eq("id", tpl.id!);
    if (error) toast.error("Delete failed");
    else {
      toast.success(isQuickMessage(tpl) ? "Quick message deleted" : "Template deleted");
      loadTemplates();
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  const selectedClinic = clinics.find(c => c.id === selectedClinicId);
  const whatsappTemplates = templates.filter((tpl) => tpl.template_kind === "whatsapp");
  const quickMessages = templates.filter((tpl) => tpl.template_kind === "quick_message");
  const editingQuickMessage = isQuickMessage(editing);

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Templates</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage WhatsApp templates and quick messages for each clinic
          </p>
        </div>
        <button
          onClick={() => openNew("whatsapp")}
          disabled={!selectedClinicId}
          className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
        >
          <Plus size={16} />
          New WhatsApp template
        </button>
      </div>

      {/* Clinic selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700 whitespace-nowrap">Clinic</label>
        {loadingClinics ? (
          <span className="text-sm text-slate-400">Loading...</span>
        ) : (
          <select
            value={selectedClinicId}
            onChange={e => setSelectedClinicId(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 min-w-56"
          >
            <option value="">- Select a clinic -</option>
            {clinics.map(c => (
              <option key={c.id} value={c.id}>{clinicLabel(c)}</option>
            ))}
          </select>
        )}
        {selectedClinic && (
          <span className="text-xs text-slate-400 font-mono">{selectedClinicId}</span>
        )}
      </div>

      {/* Table */}
      {!selectedClinicId ? (
        <div className="text-center py-16 text-slate-400">
          <p className="text-base font-medium">Select a clinic</p>
          <p className="text-sm mt-1">to view and manage its templates.</p>
        </div>
      ) : loadingTemplates ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : (
        <div className="space-y-6">
          <section className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">WhatsApp Templates</h2>
              <p className="text-sm text-slate-500">
                Templates approved in WhatsApp Business Manager for outbound messages outside the
                24-hour window.
              </p>
            </div>

            {whatsappTemplates.length === 0 ? (
              <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-xl bg-white">
                <p className="text-base font-medium">No WhatsApp templates</p>
                <p className="text-sm mt-1">
                  Add an approved template to send proactive messages from this clinic.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Display name</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Meta template name</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Language</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Variables</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Body</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Image</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {whatsappTemplates.map((tpl, i) => (
                      <tr
                        key={tpl.id}
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i === whatsappTemplates.length - 1 ? "border-b-0" : ""}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-800">{tpl.display_name}</td>
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs">{tpl.name}</td>
                        <td className="px-4 py-3 text-slate-500">{tpl.language_code || "-"}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(tpl.placeholders ?? []).map(p => (
                              <span key={p} className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full font-mono">
                                {`{{${p}}}`}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-xs text-slate-500 font-mono truncate" title={tpl.template_body}>
                            {tpl.template_body ? tpl.template_body.slice(0, 80) + (tpl.template_body.length > 80 ? "..." : "") : "-"}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          {tpl.header_media_url ? (
                            <img
                              src={tpl.header_media_url}
                              alt="header"
                              className="w-10 h-10 object-cover rounded-lg border border-slate-200"
                            />
                          ) : (
                            <span className="text-slate-300 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => openEdit(tpl)}
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                              title="Edit"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => handleDelete(tpl)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Quick Messages</h2>
                <p className="text-sm text-slate-500">
                  Predefined internal messages available in the patient conversation composer via{" "}
                  <span className="font-mono">/shortcut</span>.
                </p>
              </div>
              <button
                onClick={() => openNew("quick_message")}
                disabled={!selectedClinicId}
                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
              >
                <Plus size={16} />
                New quick message
              </button>
            </div>

            {quickMessages.length === 0 ? (
              <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-xl bg-white">
                <p className="text-base font-medium">No quick messages</p>
                <p className="text-sm mt-1">
                  Add shortcuts practitioners can insert from the conversation composer.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Display name</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Shortcut</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Message body</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {quickMessages.map((tpl, i) => (
                      <tr
                        key={tpl.id}
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i === quickMessages.length - 1 ? "border-b-0" : ""}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-800">{tpl.display_name}</td>
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs">/{tpl.shortcut}</td>
                        <td className="px-4 py-3 max-w-xl">
                          <p className="text-xs text-slate-500 whitespace-pre-wrap line-clamp-3" title={tpl.template_body}>
                            {tpl.template_body || "-"}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => openEdit(tpl)}
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                              title="Edit"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => handleDelete(tpl)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Create / edit drawer */}
      {panelOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={closePanel} />
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  {editing.id
                    ? editingQuickMessage
                      ? "Edit quick message"
                      : "Edit template"
                    : editingQuickMessage
                      ? "New quick message"
                      : "New template"}
                </h2>
                {selectedClinic && (
                  <p className="text-xs text-slate-400 mt-0.5">{clinicLabel(selectedClinic)}</p>
                )}
              </div>
              <button
                onClick={closePanel}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <div className="flex-1 px-6 py-5 space-y-5">

              {/* Display name */}
              <Field label="Display name" required hint="Label shown to the practitioner in the backoffice">
                <input
                  type="text"
                  value={editing.display_name}
                  onChange={e => setEditing(p => ({ ...p, display_name: e.target.value }))}
                  placeholder={editingQuickMessage ? "e.g. Taking over the conversation" : "e.g. Appointment reminder"}
                  className={inputClass}
                />
              </Field>

              {editingQuickMessage ? (
                <Field
                  label="Shortcut"
                  required
                  hint="Practitioners can type /shortcut in the composer to insert the message."
                >
                  <div className="flex items-center rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-slate-300">
                    <span className="px-3 text-sm font-mono text-slate-400">/</span>
                    <input
                      type="text"
                      value={editing.shortcut}
                      onChange={e =>
                        setEditing(p => ({
                          ...p,
                          shortcut: normalizeQuickMessageShortcut(e.target.value),
                        }))
                      }
                      placeholder="taking_over"
                      className="w-full rounded-r-lg px-0 py-2 pr-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none font-mono"
                    />
                  </div>
                </Field>
              ) : (
                <>
                  {/* Meta template name */}
                  <Field
                    label="Meta template name"
                    required
                    hint="Must match the name registered in WhatsApp Business Manager and is used as the identifier in code."
                  >
                    <input
                      type="text"
                      value={editing.name}
                      onChange={e => {
                        const val = e.target.value.toLowerCase().replace(/\s+/g, "_");
                        setEditing(p => ({ ...p, name: val }));
                      }}
                      placeholder="e.g. appointment_reminder"
                      className={`${inputClass} font-mono`}
                    />
                  </Field>

                  {/* Language */}
                  <Field
                    label="Language"
                    hint="BCP-47 code (en, fr, en_US...) that must match the template approved by Meta."
                  >
                    <input
                      type="text"
                      value={editing.language_code}
                      onChange={e => setEditing(p => ({ ...p, language_code: e.target.value }))}
                      placeholder="en"
                      className={`${inputClass} w-28`}
                    />
                  </Field>
                </>
              )}

              {/* Message body */}
              <Field
                label="Message body"
                required={editingQuickMessage}
                hint={
                  editingQuickMessage
                    ? "Inserted directly into the draft when the practitioner selects the shortcut."
                    : "Approved by Meta. Use {{variable}} placeholders in the same order as the variables below."
                }
              >
                <textarea
                  value={editing.template_body}
                  onChange={e => setEditing(p => ({ ...p, template_body: e.target.value }))}
                  rows={5}
                  placeholder={
                    editingQuickMessage
                      ? "Hello, I am taking over your conversation. I will review your request and get back to you shortly."
                      : "Hi {{name}},\nYour appointment is on {{date}} at {{time}}."
                  }
                  className={`${inputClass} resize-none font-mono text-xs leading-relaxed`}
                />
              </Field>

              {!editingQuickMessage && (
                <>
                  {/* Variables (placeholders) */}
                  <Field label="Variables" hint="List variables in template order. Press Enter to add one.">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={placeholderInput}
                        onChange={e => setPlaceholderInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addPlaceholder(); } }}
                        placeholder="name"
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={addPlaceholder}
                        className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm transition-colors whitespace-nowrap"
                      >
                        Add
                      </button>
                    </div>
                    {editing.placeholders.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {editing.placeholders.map(p => (
                          <span key={p} className="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2.5 py-1 rounded-full font-mono">
                            {`{{${p}}}`}
                            <button onClick={() => removePlaceholder(p)} className="text-slate-400 hover:text-red-500 ml-0.5">
                              <X size={11} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </Field>

                  {/* Twilio Content SID */}
                  <Field label="Twilio Content SID" hint="Optional. Only used for clinics sending WhatsApp templates via Twilio.">
                    <input
                      type="text"
                      value={editing.content_sid}
                      onChange={e => setEditing(p => ({ ...p, content_sid: e.target.value }))}
                      placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className={`${inputClass} font-mono text-xs`}
                    />
                  </Field>

                  {/* Header image */}
                  <Field label="Header image" hint="Required if the template has a Meta-approved image header.">
                    <div className="space-y-2">
                      {editing.header_media_url && (
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <img
                            src={editing.header_media_url}
                            alt="preview"
                            className="w-16 h-16 object-cover rounded-lg border border-slate-200 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-500 break-all leading-relaxed">{editing.header_media_url}</p>
                            <button
                              onClick={() => setEditing(p => ({ ...p, header_media_url: "" }))}
                              className="mt-1.5 text-xs text-red-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-600 text-sm rounded-lg transition-colors w-full justify-center"
                      >
                        <ImagePlus size={16} />
                        {uploading ? "Uploading..." : editing.header_media_url ? "Change image" : "Upload image"}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <input
                        type="text"
                        value={editing.header_media_url}
                        onChange={e => setEditing(p => ({ ...p, header_media_url: e.target.value }))}
                        placeholder="or paste a public URL directly"
                        className={`${inputClass} text-xs`}
                      />
                    </div>
                  </Field>
                </>
              )}

            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={closePanel}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving ? "Saving..." : editing.id ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── UI helpers ───────────────────────────────────────────────────────────────

const inputClass =
  "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition";

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {hint && <p className="text-xs text-slate-400 leading-snug">{hint}</p>}
      {children}
    </div>
  );
}
